import React, { useEffect, useState } from "react";
import {
  useTranslate,
  useApiUrl,
  useNavigation,
} from "@pankod/refine-core";
import {
  Form,
  Input,
  Select,
  useForm,
  useSelect,
  SaveButton,
} from "@pankod/refine-antd";
import { Modal } from "antd";
import axios from "axios";
import {
  IQuizAICreation,
  ILanguage,
  IQuizCategory,
  ILLMModel,
} from "interfaces";

const FormFields: React.FC = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();

  const { selectProps: languageSelectProps } = useSelect<ILanguage>({
    resource: "language",
    optionLabel: "name",
    optionValue: "lang_code",
  });

  const { selectProps: quizCategorySelectProps } = useSelect<IQuizCategory>({
    resource: "quiz-category",
    optionLabel: "name",
    optionValue: "quiz_category_id",
  });

  const [models, setModels] = useState<ILLMModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  useEffect(() => {
    const APIToken = localStorage.getItem("api-token");
    setModelsLoading(true);
    axios
      .get(`${apiUrl}/llm-models`, {
        headers: { Authorization: `Bearer ${APIToken}` },
      })
      .then((res) => setModels(res.data || []))
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [apiUrl]);

  const providers = Array.from(new Set(models.map((m) => m.provider)));

  return (
    <>
      <Form.Item
        label={t("quiz-ai-creation.fields.name")}
        name="name"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={t("quiz-ai-creation.fields.url")}
        name="url"
        rules={[
          { required: true },
          { type: "url", message: "Must be a valid URL" },
        ]}
      >
        <Input placeholder="https://..." />
      </Form.Item>

      <Form.Item
        label={t("quiz-ai-creation.fields.lang")}
        name="lang"
        rules={[{ required: true }]}
      >
        <Select {...languageSelectProps} />
      </Form.Item>

      <Form.Item
        label={t("quiz-ai-creation.fields.quiz_category_name")}
        name="quiz_category_id"
        rules={[{ required: true }]}
      >
        <Select {...quizCategorySelectProps} />
      </Form.Item>

      <Form.Item
        label={t("quiz-ai-creation.create.model_label")}
        name="model_string"
        rules={[{ required: true }]}
      >
        <Select
          loading={modelsLoading}
          placeholder={t("quiz-ai-creation.create.model_label")}
        >
          {providers.map((provider) => (
            <Select.OptGroup key={provider} label={provider.toUpperCase()}>
              {models
                .filter((m) => m.provider === provider)
                .map((m) => (
                  <Select.Option
                    key={m.id}
                    value={m.model_string}
                    disabled={!m.is_active}
                  >
                    {m.name}
                  </Select.Option>
                ))}
            </Select.OptGroup>
          ))}
        </Select>
      </Form.Item>
    </>
  );
};

interface QuizAICreationCreateModalProps {
  visible: boolean;
  onClose: () => void;
}

export const QuizAICreationCreateModal: React.FC<
  QuizAICreationCreateModalProps
> = ({ visible, onClose }) => {
  const t = useTranslate();
  const { show } = useNavigation();

  const { formProps, saveButtonProps } = useForm<IQuizAICreation>({
    action: "create",
    resource: "quiz-ai-creation",
    redirect: false,
    onMutationSuccess: (data) => {
      const created = data?.data as IQuizAICreation | undefined;
      formProps.form?.resetFields();
      onClose();
      if (created?.id) {
        show("quiz-ai-creation", created.id);
      }
    },
  });

  const handleCancel = () => {
    formProps.form?.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("quiz-ai-creation.title")}
      visible={visible}
      onCancel={handleCancel}
      footer={[<SaveButton key="save" {...saveButtonProps} />]}
      destroyOnClose
      width={640}
    >
      <Form {...formProps} layout="vertical">
        <FormFields />
      </Form>
    </Modal>
  );
};
